import { Col, GridContainer, GridItem, Spacer } from "@amzn/stencil-react-components/layout";
import { DetailedRadio } from "@amzn/stencil-react-components/radio";
import { Select } from "@amzn/stencil-react-components/select";
import { Label } from "@amzn/stencil-react-components/text";
import { trackUxmCustomEvent } from "common/helpers/analytics-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { useEffect } from "react";

export interface VersionObject {
  taxonomyId: string;
  displayText: string;
}

export interface SelectChildSkuProps {
  itemChildVersions: VersionObject[] | undefined;
  onChange: (value: VersionObject) => void;
  selectedVersion?: VersionObject;
  autoSelectFirstVersion?: boolean;
  isLegacy?: boolean;
}

export const SelectChildSku = ({
  itemChildVersions,
  onChange,
  selectedVersion,
  autoSelectFirstVersion = true,
  isLegacy = true,
}: SelectChildSkuProps) => {
  useEffect(() => {
    if (autoSelectFirstVersion && itemChildVersions && itemChildVersions.length > 0 && !selectedVersion) {
      onChange(itemChildVersions[0]);
    }
  }, [itemChildVersions, onChange, selectedVersion]);

  /* istanbul ignore next */
  const handleRadioChange = (sku: any) => {
    trackUxmCustomEvent("select-child-sku", { selectedValue: sku.displayText });
    onChange(sku);
  };

  /* istanbul ignore next */
  const handleSelectChange = (selectedValue: string) => {
    trackUxmCustomEvent("radio-child-sku", { selectedValue });
    const newSelectedSku = itemChildVersions?.find((childVersion) => childVersion.displayText === selectedValue);
    if (newSelectedSku) {
      onChange(newSelectedSku);
    }
  };

  const renderRadios = () => {
    if (itemChildVersions) {
      return itemChildVersions.map((childVersion) => (
        <GridItem lg={isLegacy ? 6 : 8} md={4} key={childVersion.taxonomyId}>
          <DetailedRadio
            dataTestId="radio-child-sku"
            titleText={childVersion.displayText}
            checked={selectedVersion?.taxonomyId === childVersion.taxonomyId}
            onChange={() => handleRadioChange(childVersion)}
          />
        </GridItem>
      ));
    }
  };

  const RADIO_TO_DROPDOWN_THRESHOLD = isLegacy ? 6 : 2;
  // Reduced threshold from 6 to 2 for non-legacy mode due to Qualtrics items having very long descriptions
  // that cause layout issues when displayed as radio buttons
  if (itemChildVersions && itemChildVersions.length <= RADIO_TO_DROPDOWN_THRESHOLD) {
    return (
      <Col>
        <Label>
          {selectedVersion?.taxonomyId && selectedVersion.taxonomyId.startsWith("atn::software")
            ? getString("details.selectChildSkuTeam")
            : getString("details.selectChildSku")}
        </Label>
        <Spacer height="S300" />
        <GridContainer>{renderRadios()}</GridContainer>
      </Col>
    );
  } else if (itemChildVersions && itemChildVersions.length > RADIO_TO_DROPDOWN_THRESHOLD) {
    const options = itemChildVersions.map((sku) => sku.displayText);
    return (
      <Col gridGap="S200">
        <Label>
          {selectedVersion?.taxonomyId && selectedVersion.taxonomyId.startsWith("atn::software")
            ? getString("details.selectChildSkuTeam")
            : getString("details.selectChildSku")}
        </Label>
        <Select
          dataTestId="select-child-sku"
          onChange={handleSelectChange}
          options={options}
          value={selectedVersion?.displayText}
          isSearchable={true}
          width="65%"
        />
      </Col>
    );
  } else {
    return null;
  }
};
